import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
    FormInput,
    FormUtils,
} from 'wanderu-component-form';
import {
    generateIsomorphicID,
    generateFakeOnChangeEvent,
} from 'wanderu-utils';

import Options from '../Options/Options';

import styles from './InputDropdown.scss';

const BEGINNING_POSITION = -1;
const DEFAULT_HIGHLIGHT_POSITION = 0;
class InputDropdown extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        'data-id': PropTypes.string,
        value: PropTypes.string,
        defaultValue: PropTypes.string,
        options: PropTypes.array,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onInputChange: PropTypes.func,
        onFocus: PropTypes.func,
        onKeyUp: PropTypes.func,
        onKeyDown: PropTypes.func,
        onMouseLeave: PropTypes.func,
        showInputCaret: PropTypes.bool,
    };

    static defaultProps = {
        className: '',
        'data-id': '',
        value: '',
        defaultValue: '',
        options: [],
        onBlur() {},
        onChange() {},
        onFocus() {},
        onInputChange() {},
        onKeyDown() {},
        onKeyUp() {},
        onMouseLeave() {},
        reactInputComponent: FormInput,
        reactOptionsComponent: Options,
        showInputCaret: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            isFocused: false,
            optionPosition: BEGINNING_POSITION,
            value: this.props.value,
        };

        this.inputId = generateIsomorphicID({
            onBlur: props.onBlur,
            onChange: props.onChange,
            options: props.options,
            seed: 'inputId',
        });
        this.optionsId = generateIsomorphicID({
            onBlur: props.onBlur,
            onChange: props.onChange,
            options: props.options,
            seed: 'optionsId',
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.value && nextProps.value !== this.state.value) {
            this.setState(() => ({
                value: nextProps.value,
            }));
        }
    }

    get _value() {
        if (this._selectedOption) {
            return this._selectedOption.text;
        }

        return this.props.defaultValue || this.state.value;
    }

    get _options() {
        return this.props.options;
    }

    get _selectedOption() {
        const { optionPosition } = this.state;
        if (optionPosition === BEGINNING_POSITION) {
            return undefined;
        }

        return {
            ...this._options[optionPosition],
            index: optionPosition,
        };
    }

    get _showOptions() {
        return this.state.isFocused && this._options.length !== 0;
    }

    _augmentChildren() {
        return FormUtils.childrenToArray(this.props.children)
            .map((child) => this._augmentChild(child));
    }

    _augmentChild(child) {
        if (!child) {
            return child;
        }

        if (this._showOptions
            && FormUtils.childIsInstanceOf(child, this.props.reactOptionsComponent)
        ) {
            return this._augmentOptions(child);
        }

        if (FormUtils.childIsInput(child, this.props.reactInputComponent)) {
            return this._augmentInput(child);
        }
        return child;
    }

    _augmentOptions(options) {
        let newProps = {
            onMouseEnter:
                FormUtils.augmentChildEvent(this._onOptionsMouseEnter, options.props.onMouseEnter),
            onMouseLeave:
                FormUtils.augmentChildEvent(this._onOptionsMouseLeave, options.props.onMouseLeave),
            options: this.props.options,
            position: this.state.optionPosition,
            match: this.state.value,
            showOptions: this.state.isFocused,
            key: this.optionsId,
        };

        return React.cloneElement(options, newProps);
    }

    _onOptionsMouseEnter = (optionPosition) => {
        this.setState(() => ({ optionPosition }));
    };

    _onOptionsMouseLeave = (e) => {
        this.setState(() => ({ optionPosition: BEGINNING_POSITION }));
        this.props.onMouseLeave(e);
    }

    _augmentInput(input) {
        let newProps = {
            onBlur: FormUtils.augmentChildEvent(this._onInputBlur, input.props.onBlur),
            onChange: FormUtils.augmentChildEvent(this._onInputChange, input.props.onChange),
            onFocus: FormUtils.augmentChildEvent(this._onInputFocus, input.props.onFocus),
            onKeyUp: FormUtils.augmentChildEvent(this._onInputKeyUp, input.props.onKeyUp),
            onKeyDown: FormUtils.augmentChildEvent(this._onInputKeyDown, input.props.onKeyDown),
            value: this.state.value,
            key: this.inputId,
        };

        return React.cloneElement(input, newProps);
    }

    _setExistingOption() {
        const position = this._options.findIndex((option) => {
            return option.text === this.state.value;
        });

        this.setState(() => ({ optionPosition: position }));
    }

    _onInputFocus = (e) => {
        this.setState(() => ({
            isFocused: true,
        }));

        this._setExistingOption();
        this.props.onFocus(e);
    };

    _onInputBlur = (e) => {
        e.persist();
        const fakeEvent = generateFakeOnChangeEvent(e, {
            ...this._selectedOption,
            userText: this.state.value,
        });

        this.setState(
            () => ({
                isFocused: false,
                value: this._value,
            }),
            // TS: Allow consumers to set state in response to the change event
            //    before emitting the blur event
            () => this.props.onBlur(fakeEvent),
        );

        if (!this._selectedOption) {
            const fakeChangeEvent = generateFakeOnChangeEvent(e, this._value);
            return this.props.onInputChange(fakeChangeEvent);
        }

        this.props.onChange(fakeEvent);
    };

    _onInputChange = (e) => {
        this.props.onInputChange(e);

        let value = e.target.value;

        this.setState(() => ({ value }));

        const { optionPosition } = this.state;

        if (!value) {
            return this.setState(() => ({ optionPosition: BEGINNING_POSITION }));
        }

        if (optionPosition === BEGINNING_POSITION) {
            return this.setState(() => ({ optionPosition: DEFAULT_HIGHLIGHT_POSITION }));
        }
    };

    _onInputKeyDown = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            this._preventFormSubmit(e);
            return this._blurFocusedInput();
        }

        if (key === 'Backspace') {
            // TODO ask ani about -1 or 0;
            this.setState(() => ({ optionPosition: -1 }));
        }

        this.props.onKeyDown(e);
    }

    _preventFormSubmit(e) {
        return e.preventDefault();
    }

    _blurFocusedInput() {
        document.activeElement.blur();
    }

    _onInputKeyUp = (e) => {
        const { key } = e;
        if (!['ArrowUp', 'ArrowDown'].includes(key)) {
            this.props.onKeyUp(e);
            return;
        }

        let optionPosition = this.state.optionPosition;

        if (key === 'ArrowUp') {
            optionPosition -= 1;
        }

        if (key === 'ArrowDown') {
            optionPosition += 1;
        }

        optionPosition = this._wrapOptionPosition(optionPosition);
        this.setState(() => ({ optionPosition }));

        this.props.onKeyUp(e);
    };

    _wrapOptionPosition(currentPosition) {
        const { options } = this.props;
        if (currentPosition < BEGINNING_POSITION) {
            const lastPosition = options.length - 1;
            return lastPosition;
        }

        if (currentPosition === options.length) {
            return BEGINNING_POSITION;
        }

        return currentPosition;
    }

    render() {
        return (
            <div
                className={classNames(
                    this.props.className,
                    styles.container,
                    { [styles.isFocused]: this.state.isFocused },
                    { [styles.showInputCaret]: this.props.showInputCaret },
                )}
                data-id={this.props['data-id']}
            >
                {this._augmentChildren()}
            </div>
        );
    }
}

export default InputDropdown;
